export interface ShiftRangeOptions {
  /** Row the user shift-clicked. */
  index: number;
  /** Rows already selected, as indices into the rendered list. */
  selectedIndices: number[];
  renderedCount: number;
}

/**
 * Rows a shift-click should add, given the current selection.
 *
 * Only ever grows the selection: a shift-click inside an already-selected run
 * returns nothing rather than silently dropping rows the user ticked.
 */
export function resolveShiftRange({ index, selectedIndices, renderedCount }: ShiftRangeOptions): number[] {
  if (index < 0 || index >= renderedCount) return [];

  const inRange = selectedIndices.filter((value) => value >= 0 && value < renderedCount);
  if (inRange.length === 0) return [index];

  const sorted = [...new Set(inRange)].sort((a, b) => a - b);
  const low = sorted[0]!;
  const high = sorted[sorted.length - 1]!;
  const isSingleRun = high - low + 1 === sorted.length;

  // With gaps there is no unambiguous run to grow, so extend from the nearest
  // selected row instead of guessing which side the user meant.
  const anchor = isSingleRun
    ? (index < low ? high : low)
    : sorted.reduce((closest, value) => (Math.abs(value - index) < Math.abs(closest - index) ? value : closest), sorted[0]!);

  if (index >= low && index <= high && isSingleRun) return [];

  const start = Math.min(anchor, index);
  const end = Math.max(anchor, index);
  const range: number[] = [];
  for (let cursor = start; cursor <= end; cursor += 1) range.push(cursor);
  return range;
}
